import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Lock, Mail, Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LOGO_DARK_URL } from "../lib/brand";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [mode, setMode] = useState("login"); // login | forgot-email | forgot-otp | forgot-done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const nav = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: resetEmail });
      if (data.dev_otp) {
        setDevOtp(data.dev_otp);
        toast.success(`OTP generated — shown on screen (dev mode)`, { duration: 8000 });
      } else {
        setDevOtp("");
        toast.success("OTP sent to your email");
      }
      setMode("forgot-otp");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        email: resetEmail, otp, new_password: newPassword,
      });
      toast.success("Password reset. Please login with the new password.");
      setMode("login");
      setEmail(resetEmail);
      setPassword("");
      setOtp("");
      setDevOtp("");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col bg-carbon-950 text-white relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14704783/pexels-photo-14704783.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative z-10 p-12 flex flex-col h-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-black p-2 border border-slate-700">
              <img src={LOGO_DARK_URL} alt="Universal EE" className="h-12 w-auto" />
            </div>
          </Link>
          <div className="mt-auto">
            <div className="overline text-amber-400 mb-3">Admin / Staff Portal</div>
            <h2 className="font-heading text-4xl font-black tracking-tight max-w-md leading-tight">
              Field reports, expenses &amp; PDF documentation — all in one place.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md text-sm leading-relaxed">
              Submit service, installation &amp; vehicle reports. Log field expenses.
              Every submission generates a branded company PDF.
            </p>
          </div>
        </div>
      </div>

      {/* Right forms */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          {mode === "login" && (
            <>
              <div className="overline mb-3">Sign In</div>
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-2">Welcome back</h1>
              <p className="text-slate-600 text-sm mb-8">Sign in with your work credentials.</p>
              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="login-email-input" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 h-11 rounded-none" autoComplete="email" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="login-password-input" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 h-11 rounded-none" autoComplete="current-password" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setMode("forgot-email"); setResetEmail(email); }}
                    className="text-xs font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700"
                    data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
                <Button data-testid="login-submit-btn" type="submit" disabled={submitting}
                  className="w-full h-11 rounded-none btn-amber font-semibold">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Sign in
                </Button>
              </form>
            </>
          )}

          {mode === "forgot-email" && (
            <>
              <button onClick={() => setMode("login")} className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-amber-600 mb-5 inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to login
              </button>
              <div className="overline mb-3">Password Reset — Step 1 of 2</div>
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-2">Forgot password?</h1>
              <p className="text-slate-600 text-sm mb-8">Enter your admin email to receive a 6-digit OTP.</p>
              <form onSubmit={requestOtp} className="space-y-5" data-testid="forgot-email-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Admin Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="forgot-email-input" type="email" required value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-9 h-11 rounded-none" />
                  </div>
                </div>
                <Button data-testid="forgot-email-submit" type="submit" disabled={submitting}
                  className="w-full h-11 rounded-none btn-amber font-semibold">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send OTP
                </Button>
              </form>
            </>
          )}

          {mode === "forgot-otp" && (
            <>
              <button onClick={() => setMode("forgot-email")} className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-amber-600 mb-5 inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <div className="overline mb-3">Password Reset — Step 2 of 2</div>
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-2">Verify &amp; Reset</h1>
              <p className="text-slate-600 text-sm mb-6">
                Enter the OTP sent to <span className="font-mono font-bold">{resetEmail}</span> and set your new password.
              </p>
              {devOtp && (
                <div data-testid="dev-otp-banner" className="mb-6 border-2 border-amber-400 bg-amber-50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                    Dev mode — your OTP (SMTP not configured)
                  </div>
                  <div className="font-mono font-black text-2xl tracking-[0.4em] text-slate-900 mb-2">{devOtp}</div>
                  <div className="text-[11px] text-amber-700">
                    Copy this OTP and paste it below. In production, this banner will not appear — you'll receive the
                    OTP by email instead.
                  </div>
                  <button
                    type="button"
                    onClick={() => { setOtp(devOtp); }}
                    className="mt-2 text-xs font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 underline"
                    data-testid="dev-otp-paste-btn"
                  >
                    Paste it into the field ↓
                  </button>
                </div>
              )}
              <form onSubmit={resetPw} className="space-y-5" data-testid="reset-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">OTP (6 digits)</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="otp-input" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      className="pl-9 h-11 rounded-none font-mono tracking-[0.3em] text-lg"
                      placeholder="••••••"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="new-password-input" type="password" required minLength={6} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-9 h-11 rounded-none" />
                  </div>
                </div>
                <Button data-testid="reset-submit" type="submit" disabled={submitting}
                  className="w-full h-11 rounded-none btn-amber font-semibold">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Reset Password
                </Button>
              </form>
            </>
          )}

          <Link to="/" className="block mt-6 text-sm text-slate-600 hover:text-amber-600">
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
